import { React, Fragment, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import BodyMask from "components/utility/body-mask/BodyMask";
import AccordionDrawer from "./AccordionDrawer";
import DocumentLink from "./DocumentLink";
import ImageDownload from "components/utility/images/ImageDownload";
import ImageLinkOut from "components/utility/images/ImageLinkOut";
import { isDesktop } from "components/utility/page-responsive/PageResponsive";

const GeneralDocumentsDrawer = (props) => {
	const [genDocsDrawerHeight, setGenDocsDrawerHeight] = useState(0);
	const [isDesktopWidth, setIsDesktopWidth] = useState(0);
	const [isExpanded, setIsExpanded] = useState(false);
	const [scrollProps, setScrollProperties] = useState({
		scrollTop: null,
		originalStyles: null
	});

	const data = props.data ? props.data : null;
	const links = props.data ? props.data.links : null;
	const rowRepeatCount = links ? Math.ceil(links.length / 2) : 0;
	const listItemIcon = (type) => {
		if (type === "download") {
			return <ImageDownload />;
		}
		if (type === "external") {
			return <ImageLinkOut />;
		}
		return null; // no icon for internal links
	};

	const toggleDrawer = () => {
		setIsExpanded(!isExpanded);
		toggleScroll(!isExpanded);
	};

	const toggleScroll = (disable) => {
		const scrollContainer = document.scrollingElement || document.documentElement;
		if (disable) {
			// disable scroll
			const scrollTop = scrollContainer.scrollTop;
			const originalStyles = document.body.style.cssText; // preserve styles for enable scroll
			setScrollProperties({
				scrollTop,
				originalStyles
			});
			document.body.style.cssText =
				";" +
				("overflow: hidden; position: fixed; height:   100%; width:    100%; top:      " +
					-scrollTop +
					"px; ");
		} else {
			// enable scroll
			document.body.style.cssText = scrollProps.originalStyles;
			scrollContainer.scrollTop = scrollProps.scrollTop;
		}
	};

	const page = document.querySelector(".main");
	const genDocsDrawerRef = useRef(null);
	const footer = document.querySelector("footer");

	useEffect(() => {
		const genDocsDrawer = genDocsDrawerRef?.current;
		const setResponsive = () => {
			const widthIsDesktop = isDesktop();
			setIsDesktopWidth(widthIsDesktop);
			if (widthIsDesktop) {
				page.style.paddingBottom = "0px"; // remove bottom page padding when drawer disappears upon resize to desktop
			}
		};

		// initialize isDesktopWidth on mount/page load
		if (isDesktopWidth === 0) {
			setResponsive();
		}
		window.addEventListener("resize", setResponsive);

		const footerIntersectionObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					page.style.paddingBottom = "0px";
					genDocsDrawer.style.position = "static";
				} else {
					page.style.paddingBottom = genDocsDrawerHeight + "px";
					genDocsDrawer.style.position = "fixed";
				}
			});
		});
		const genDocsDrawerHeightObserver = new ResizeObserver((entries) => {
			entries.forEach((entry) => {
				setGenDocsDrawerHeight(Math.floor(entry.contentRect.height));
			});
		});

		// Toggle general docs drawer activation if window width is <1024
		if (isDesktopWidth === false) {
			if (genDocsDrawer && page) {
				footerIntersectionObserver.observe(footer);
				genDocsDrawerHeightObserver.observe(genDocsDrawer);
			}
		}

		return () => {
			window.removeEventListener("resize", setResponsive);
			footerIntersectionObserver.disconnect(footer);
			genDocsDrawerHeightObserver.disconnect(genDocsDrawer);
		};
	}, [genDocsDrawerRef, genDocsDrawerHeight, footer, page, isDesktopWidth]);

	return data && links ? (
		<Fragment>
			<BodyMask
				className={"general-documents-scroll-mask " + (isExpanded ? " body-mask " : "")}
				onClick={() => toggleDrawer()}
			/>
			<div
				ref={genDocsDrawerRef}
				id="general-documents-drawer"
				className={"general-documents-drawer " + (isExpanded ? " element-on-mask " : "")}
			>
				<AccordionDrawer
					data={data}
					uid="general-documents"
					expandOverrideStatus={isExpanded}
					expandOverrideControl={toggleDrawer}
					animDuration={100}
					isDrawerToggleable={links ? true : false}
				>
					<ul
						style={{
							gridTemplateRows: `repeat(${rowRepeatCount}, minmax(min-content, max-content))`
						}}
					>
						{links.map((item, index) => {
							const icon = listItemIcon(item.type);
							return <DocumentLink key={index} item={item} icon={icon} />;
						})}
					</ul>
				</AccordionDrawer>
			</div>
		</Fragment>
	) : null;
};

GeneralDocumentsDrawer.propTypes = {
	data: PropTypes.shape({
		title: PropTypes.string,
		links: PropTypes.array
	})
};

export default GeneralDocumentsDrawer;
